import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { environment } from '../../../environments/environment';
import { MatMenuTrigger } from '@angular/material/menu';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterLink,
} from '@angular/router';

import { HistoryChangesService } from '@services/Logic/historyChanges.service';
import { StateService } from '@services/Logic/state.service';
import { StorageService } from '@services/Logic/storage.service';
import { TranslateService } from '@services/Logic/translate.service';
import { UserSettingsService } from '@services/http/userSettings.service';

import { Subscription } from 'rxjs';
import { VerticalLinkBoxComponent } from '@components/headerNotAuthenticated/verticalLinkBox/verticalLinkBox';
import { SidebarComponent } from '@components/sidebar/sidebar.component';
import { SharedPipeModule } from '@app/shared/pipes/shared.pipe.module';
import { MatBadgeModule } from '@angular/material/badge';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

@Component({
  imports: [
    CommonModule,
    MatBadgeModule,
    MatIconModule,
    RouterLink,
    SharedPipeModule,
    SidebarComponent,
  ],
  standalone: true,
  selector: 'app-header-authenticated',
  templateUrl: './header-authenticated.component.html',
  styleUrls: ['./header-authenticated.component.scss'],
})
export class HeaderAuthenticatedComponent implements OnInit, OnDestroy {
  @Input() showHeader: boolean;
  @Input() newMessagesCount: number;
  @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;
  @ViewChild(VerticalLinkBoxComponent)
  verticalLinkBoxComponent: VerticalLinkBoxComponent;

  closeSidebarComponentRef: Subscription;
  showNewNotificationsHeaderComponentRef: Subscription;
  initNewNotificationsHeaderComponentRef: Subscription;
  readNotificationComponentRef: Subscription;
  initLanguageRef: Subscription;

  newNotificationsCount: number;
  activeTab: number;
  languages: any[];
  index = 0;
  urlEqualsLp = false;

  // Inside the class definition
  private MAX_NOTIFICATIONS_COUNT = 99;

  logoRouterLink = '';
  defaultImage = `${environment.basePath}assets/svg/logo.svg`;
  logo = `${environment.cloudFrontImgUrl}/private/lp/webp/hh_logo_blue.webp`;

  // needed to force trigger pipe on language change.
  currentTranslationLanguageTrigger: string;

  /**
   * URL param. Redirect URL after authentification.
   */
  redirectUrl: string;

  constructor(
    private historyChangesService: HistoryChangesService,
    private route: ActivatedRoute,
    private router: Router,
    private stateService: StateService,
    private translateService: TranslateService,
    public storage: StorageService,
    private userSettingsService: UserSettingsService,
  ) {}

  get lang() {
    return this.storage.lang;
  }

  ngOnInit() {
    this.languages = [
      { name: 'Danish', value: 'dk', flag: 'dk' },
      { name: 'English', value: 'en', flag: 'gb' },
    ];
    this.newNotificationsCount = 0;

    if (this.storage.lang) {
      if (this.storage.lang.value !== 'dk') {
        this.setLanguage(this.lang, false);
      }
    } else if (!this.lang) {
      this.storage.lang = this.languages[0];
      this.setLanguage(this.lang, false, true);
    }

    this.setRouterLink();
    this.handleAuthParams();
    this.initSubscriptions();
    this.detectHistoryChanges();
  }

  initSubscriptions() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) this.initHeaderTab();
    });

    this.closeSidebarComponentRef =
      this.stateService.closeSidebarComponentSource.subscribe(() => {
        this.initHeaderTab();
      });

    this.showNewNotificationsHeaderComponentRef =
      this.stateService.showNewNotificationsHeaderComponentSource.subscribe(
        (res) => {
          // Default is number count from API - If socket updates this typeof {} then ++
          if (Number.isInteger(res)) {
            this.newNotificationsCount = res;
            // Ensure newNotificationsCount does not exceed MAX_NOTIFICATIONS_COUNT
            if (this.newNotificationsCount > this.MAX_NOTIFICATIONS_COUNT) {
              this.newNotificationsCount = this.MAX_NOTIFICATIONS_COUNT;
            }
          }
        },
      );

    this.initNewNotificationsHeaderComponentRef =
      this.stateService.initNewNotificationsHeaderComponentSource.subscribe(
        (res) => {
          this.newNotificationsCount = res;
          // Ensure newNotificationsCount does not exceed MAX_NOTIFICATIONS_COUNT
          if (this.newNotificationsCount > this.MAX_NOTIFICATIONS_COUNT) {
            this.newNotificationsCount = this.MAX_NOTIFICATIONS_COUNT;
          }
        },
      );

    this.readNotificationComponentRef =
      this.stateService.readNotificationComponentSource.subscribe(() => {
        if (this.newNotificationsCount > 0) this.newNotificationsCount -= 1;
      });

    // Ensure newNotificationsCount does not exceed MAX_NOTIFICATIONS_COUNT
    if (this.newNotificationsCount > this.MAX_NOTIFICATIONS_COUNT) {
      this.newNotificationsCount = this.MAX_NOTIFICATIONS_COUNT;
    }

    this.initLanguageRef = this.stateService.initLanguageSource.subscribe(
      (res) => {
        if (res.lang) this.initLanguage(res.lang);
      },
    );
  }

  onImgError = (event) =>
    (event.target.src = `${environment.cloudFrontImgUrl}/private/Group.svg`);

  openSidebar() {
    this.stateService.showSidebar(4);
  }

  openSidebarNotifications() {
    this.stateService.showSidebar(3);
  }

  ngOnDestroy() {
    this.closeSidebarComponentRef?.unsubscribe();
    this.showNewNotificationsHeaderComponentRef?.unsubscribe();
    this.initNewNotificationsHeaderComponentRef?.unsubscribe();
    this.readNotificationComponentRef?.unsubscribe();
    this.initLanguageRef?.unsubscribe();
  }

  initHeaderTab() {
    const url = this.router.url.split('?')[0];
    this.urlEqualsLp = false;
    if (url.startsWith('/opgaver/mine-opgaver')) {
      this.activeTab = 1;
    } else if (url.startsWith('/opgaver')) {
      this.activeTab = 0;
    } else if (url === '/beskeder') {
      this.activeTab = 2;
    } else if (url === '/indstillinger') {
      this.activeTab = 4;
    } else if (url === '/hjaelp') {
      this.activeTab = 5;
    } else {
      this.activeTab = -1;
    }
  }

  initLanguage(value: string) {
    const lang = this.languages.find((item) => item.value === value);
    if (lang && this.lang && lang.value !== this.lang.value) {
      this.setLanguage(lang);
    }
  }

  setLanguage(lang, reloadPage: boolean = true, firstCall = false) {
    try {
      if (firstCall) return;
      if (lang !== this.storage?.userSettings?.selectedLanguage) {
        this.userSettingsService
          .changeSettings({
            type: 'selectedLanguage',
            value: lang?.value ?? this.languages[0].value,
          })
          .subscribe(() => {});
      }
      this.changeLocalLanguage(lang, reloadPage);
    } catch (error) {
      console.log(error);
    }
  }

  detectHistoryChanges() {
    this.historyChangesService.detectHistoryChanges();
  }

  public changeLocalLanguage(lang, reloadPage: boolean = true) {
    this.storage.lang = lang;
    this.translateService.use(this.lang?.value);
    this.currentTranslationLanguageTrigger = this.storage.lang.value;

    if (reloadPage) {
      window.location.reload();
    }
  }

  public changeTab(index) {
    this.activeTab = index;
    if (this.activeTab === 0) {
      this.router.navigate(['/opgaver']);
    } else if (this.activeTab === 1) {
      this.router.navigate(['/opgaver/mine-opgaver']);
    } else if (this.activeTab === 2) {
      this.router.navigate(['/beskeder']);
    } else if (this.activeTab === 3 || this.activeTab === 4) {
      this.stateService.showSidebar(this.activeTab);
    } else if (this.activeTab === 5) {
      this.router.navigate(['/hjaelp']);
    }
  }

  navigateTo(path: string) {
    this.router.navigate([path]);
  }

  setRouterLink() {
    if (this.storage?.userSettings?.pinployeeOrTaskOwner !== 'pp') {
      this.logoRouterLink = '/udforsk';
    } else {
      this.logoRouterLink = '/opgaver';
    }
  }

  switchPage() {
    const url = this.router.url.split('?')[0];
    this.navigateTo('/udforsk');
    this.logoRouterLink = '/udforsk';
    if (url !== '/') {
      this.logoRouterLink = '/';
      this.navigateTo('/');
    } else {
      const element = document.getElementsByClassName('landing_container')[0];
      element.scrollTop = 0;
      this.logoRouterLink = '/';
    }
  }

  hideBadge(item: string) {
    return (
      (item !== 'notifications' && item !== 'forum') ||
      (item === 'forum' && this.newMessagesCount === 0) ||
      (item === 'notifications' && this.newNotificationsCount === 0)
    );
  }

  /**
   * If auth query params, open authentification dialog, and redirect user on close.
   */
  private handleAuthParams() {
    this.route.queryParams.subscribe((params) => {
      this.redirectUrl = params.redirectUrl;
      if (params.authDialog != null) {
        if (this.redirectUrl) {
          this.router.navigateByUrl(this.redirectUrl);
        }
      }
    });
  }
}
